<template>
    <main class="load">
        <router-link class="load__link load__link_back link link_red"
            v-if="isReportTotalLinkVisible"
            :to="{ name: 'LoadTotal'}"
        >
            Вернуться к общим данным об эффективности
        </router-link>
        <form class="load__settings" @submit.prevent>
            <h2 class="sr-only">Настройки отчета "Эффективность"</h2>
            <daterange-picker class="load__daterange"
                v-model="reportDaterangeModel"
                name="reportDaterange"
            >
                Период отчета:
            </daterange-picker>
            <fieldset class="load__radio-group load__radio-group_modes">
                <legend class="load__legend sr-only">Режим отчета</legend>
                <radio class="load__radio load__radio_mode"
                    v-for="mode in modes"
                    :key="mode.id"
                    :val="mode.id"
                    name="mode"
                    v-model="modeModel"
                >
                    {{mode.title}}
                </radio>
            </fieldset>
            <daterange-picker class="load__daterange"
                v-if="modeId === 'compareOtherPeriod'"
                v-model="compareDaterangeModel"
                name="compareOtherPeriod"
            >
                Период сравнения:
            </daterange-picker>
            <load-settings class="load-total__settings"  v-if="isShowRestaurantsFilter" />
        </form>
        <spinner class="load__spinner" v-if="status === 'loading'" />
        <error-message class="load__error-message" v-else-if="status === 'error'">
            Не удалось загрузить данные об эффективности.
        </error-message>
        <router-view
            class="load__data"
            :status="status"
            :reportDaterange="reportDaterange"
            :compareDaterange="compareDaterange"
            :modes="modes"
            :modeId="modeId"
            :reportLoad="reportLoad"
            :compareLoad="compareLoad"
            :isReportTotalLinkVisible="isReportTotalLinkVisible"
            :isShowRestaurantsFilter="isShowRestaurantsFilter"
            :userRestaurants="userRestaurants"
        />
    </main>
</template>

<script>
    import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
    import { daterangeKey, isDaterangeSet } from "@/helpers/daterange";
    import LoadSettings from "./load/common/LoadSettings";
    import DaterangePicker from "@/components/DaterangePicker";

    export default {
        name: "Load",
        data() {
            return {
                status: "loading"
            }
        },
        components: {
            LoadSettings,
            DaterangePicker
        },
        computed: {
            ...mapState({
                reportDaterange: state => state.report.daterange,
                reportRestaurants: state => state.report.restaurants,
                compareDaterange: state => state.load.compareDaterange,
                modeId: state => state.load.modeId,
                data: state => state.load.data,
                isReportTotalLinkVisible: state => state.ui.isReportTotalLinkVisible,
                isShowRestaurantsFilter: state => state.ui.isShowRestaurantsFilter,
            }),
            ...mapGetters([
                "userRestaurants",
                "reportRestaurantsKey",
                "reportKey",
            ]),
            modes() {
                return [
                    { id: "base", title: "Базовые данные" },
                    { id: "compareOtherPeriod", title: "Сравнение с другим периодом" }
                ];
            },
            reportDaterangeModel: {
                get() {
                    return this.reportDaterange;
                },
                set(daterange) {
                    this.setReportDaterange(daterange);
                }
            },
            compareDaterangeModel: {
                get() {
                    return this.compareDaterange;
                },
                set(daterange) {
                    this.setLoadCompareDaterange(daterange);
                }
            },
            modeModel: {
                get() {
                    return this.modeId;
                },
                set(modeId) {
                    this.setLoadMode(modeId);
                }
            },
            requiredDateranges() {
                let dateranges = [this.reportDaterange];

                if (this.modeId === "compareOtherPeriod" && isDaterangeSet(this.compareDaterange)) {
                    dateranges.push(this.compareDaterange);
                }

                return dateranges;
            },
            requiredLoads() {
                return this.requiredDateranges?.map(daterange => this.data[`${daterangeKey(daterange)}-${this.reportRestaurantsKey}`]);
            },
            reportLoad() {
                return this.data[this.reportKey];
            },
            compareLoad() {
                if (isDaterangeSet(this.compareDaterange)) {
                    return this.data[`${daterangeKey(this.compareDaterange)}-${this.reportRestaurantsKey}`];
                }
            }
        },
        methods: {
            ...mapMutations([
                "setLoadMode",
                "setReportDaterange",
                "setLoadCompareDaterange"
            ]),
            ...mapActions([
                "requestLoads"
            ]),
            showReport() {
                this.status = "success";
            },
            loadLoads() {
                this.status = "loading";

                let daterangesWithoutData = this.requiredDateranges.filter(
                    daterange => !Boolean(this.data[`${daterangeKey(daterange)}-${this.reportRestaurantsKey}`])
                );

                this.requestLoads({
                    dateranges: daterangesWithoutData,
                    restaurants: this.reportRestaurants,
                    restaurantsKey: this.reportRestaurantsKey,
                }).then(() => {
                    this.showReport();
                }).catch(() => {
                    this.status = "error";
                });
            }
        },
        created() {
            if (this.userRestaurants?.ids?.length === 1) {
                this.$router.push({
                    name: "LoadRestaurant",
                    params:{
                        id: this.userRestaurants?.ids[0]
                    }
                });
            } else {
                if (this.reportRestaurants.length === 0) {
                    this.status = "no-restaurants";
                } else if (this.requiredLoads?.every(Boolean)) {
                    this.showReport();
                } else {
                    this.loadLoads();
                }
            }
        },
        watch: {
            requiredLoads(requiredLoads) {
                if (this.reportRestaurants.length === 0) {
                    this.status = "no-restaurants";
                } else if (!requiredLoads.every(Boolean)) {
                    this.loadLoads();
                } else {
                    this.showReport();
                }
            }
        }
    }
</script>

<style lang="scss">
    .load__link {
        &_back {
            display: inline-block;
            margin-bottom: 10px;

            @include desktop {
                margin-bottom: 15px;
            }
        }
    }
    .load__daterange {
        margin-bottom: 10px;

        @include desktop {
            margin-bottom: 15px;
        }
    }
    .load__radio-group {
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: flex-start;
        margin-bottom: 10px;

        @include desktop {
            flex-direction: row;
            margin-bottom: 15px;
        }
    }
    .load__radio {
        & + & {
            margin-top: 5px;

            @include desktop {
                margin: 0 0 0 20px;
            }
        }
    }
    .load__legend {
        margin-bottom: 5px;

        @include desktop {
            margin-bottom: 10px;
        }
    }
</style>
